import { ILibraryListScope, IScope, ScopeStatus } from "@/api";
import { useProcessingScopes } from "@/contexts/frontend/processing-scopes/useProcessingScopes";
import { Chip } from "@mui/material";
import { useEffect } from "react";

const STATUS_MAP: Record<
    ScopeStatus,
    { label: string; color: string; bgColor: string }
> = {
    [ScopeStatus.PENDING]: {
        label: "Waiting for Document",
        color: "#1e40af",
        bgColor: "#dbeafe",
    },
    [ScopeStatus.PROCESSING]: {
        label: "In Progress",
        color: "#92400e",
        bgColor: "#fef3c7",
    },
    [ScopeStatus.COMPLETE]: {
        label: "Completed",
        color: "#166534",
        bgColor: "#dcfce7",
    },
    [ScopeStatus.FAILED]: {
        label: "Failed",
        color: "#AF0101",
        bgColor: "#fef2f2",
    },
};

const pulseKeyframes = {
    "@keyframes pulse": {
        "0%": { opacity: 1 },
        "50%": { opacity: 0.6 },
        "100%": { opacity: 1 },
    },
};

interface IProps {
    scope: IScope | ILibraryListScope;
}

export const ScopeStatusCell = ({ scope }: IProps) => {
    const { addProcessingScope } = useProcessingScopes();
    const isProcessing =
        scope.status === ScopeStatus.PROCESSING ||
        scope.status === ScopeStatus.PENDING;

    useEffect(() => {
        if (isProcessing) {
            addProcessingScope(scope);
        }
    }, [addProcessingScope, isProcessing, scope]);

    const statusInfo = STATUS_MAP[scope.status] ?? {
        label: "Unknown",
        color: "default",
        bgColor: undefined,
    };

    return (
        <Chip
            label={statusInfo.label}
            size="small"
            sx={{
                ...(isProcessing && {
                    ...pulseKeyframes,
                    animation: "pulse 1.5s ease-in-out infinite",
                }),
                color: statusInfo.color,
                bgcolor: statusInfo.bgColor,
                borderRadius: 3,

                "& .MuiChip-label": {
                    padding: "4px 8px",
                    lineHeight: 1,
                    fontSize: 12,
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.25px",
                },
            }}
        />
    );
};
